import { extractPdfVisibility } from '@framework/engine/schema/directives/pdf-visibility/extractPdfVisibility.ts';
import { PdfVisibilityDirectiveKind } from '@framework/engine/schema/directives/pdf-visibility/IPdfVisibilityDirective.ts';
import { useWidgetScopeContext } from '@framework/engine/schema/form/fillable/WidgetScopeContext.tsx';
import { WidgetComponentContextProvider } from '@framework/engine/schema/widgets/context/WidgetComponentContext.tsx';
import { type IWidgetComponentPropsConfig } from '@framework/engine/schema/widgets/IWidgetType.ts';
import { WidgetRepository } from '@framework/engine/schema/widgets/repository/WidgetRepository.ts';
import { type Widget } from '@framework/engine/schema/widgets/Widget.ts';
import { getWidgetPath } from '@framework/engine/validator/form-builder/getWidgetPath.ts';
import React, { useMemo } from 'react';
import { InputContainer } from './InputContainer.tsx';

export interface IFormComponentProps {
  widget: Widget;
  children?: React.ReactNode;
  componentLayout?: IWidgetComponentPropsConfig;
  /**
   * Rendering in modalità readonly (riepilogo, integrazioni, ...)
   */
  readonly?: boolean;
  /**
   * In caso di modalità readonly, specifica se il widget è in sola lettura a
   * livello di singolo widget, nello specifico serve per le Integrazioni dove
   * _alcuni_ campi non sono modificabili.
   * Questo a livello di UI si traduce in campi _disabilitati_ (disabled).
   */
  isOnlyWidgetReadonly?: boolean;
}

/**
 * Componente che, dato un elemento, ne restituisce il componente di form
 * recuperandolo dal widgetRepository.
 */
export const FormComponent = React.memo(
  (props: IFormComponentProps) => {
    const {
      widget,
      children,
      componentLayout,
      readonly,
      isOnlyWidgetReadonly,
    } = props;
    const widgetComponent = WidgetRepository.find(widget.type);
    // Recupero il prefisso dal contesto, generato da alcuni widget (es. repeatable)
    const { prefix } = useWidgetScopeContext();

    const isCompilationVisible = useMemo(() => {
      const visibility = extractPdfVisibility(widget);
      if (!visibility) return true;
      return visibility.visibility !== PdfVisibilityDirectiveKind.PDF;
    }, [widget]);

    if (!widgetComponent) {
      console.warn(`No widget found for type ${widget.type}`, { widget });
      return null;
    }

    /**
     * Se il widget è indicato come solo visibile nel pdf, non viene renderizzato.
     */
    if (!isCompilationVisible) {
      return null;
    }

    /**
     * Scelta del componente da renderizzare. Può essere readonly (per
     * il riepilogo) o form (per la compilazione).
     * Se readonlyComponent non è definito, viene utilizzato il formComponent
     * a cui viene passato il parametro `readonly`, in caso le modifiche siano
     * minime.
     */
    const RenderingComponent =
      readonly && widgetComponent.readonlyComponent
        ? widgetComponent.readonlyComponent
        : widgetComponent.formComponent;

    const widgetFormName = getWidgetPath(widget, prefix);

    return (
      <InputContainer>
        <WidgetComponentContextProvider
          name={widgetFormName}
          isOnlyWidgetReadonly={isOnlyWidgetReadonly}
        >
          <RenderingComponent
            widget={widget}
            componentLayout={componentLayout}
            name={widgetFormName}
            readonly={readonly}
          >
            {children}
          </RenderingComponent>
        </WidgetComponentContextProvider>
      </InputContainer>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.widget === nextProps.widget &&
      prevProps.readonly === nextProps.readonly &&
      prevProps.isOnlyWidgetReadonly === nextProps.isOnlyWidgetReadonly
    );
  },
);

FormComponent.displayName = 'FormComponent';
